import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

export const OTPInput = ({ 
  length = 6, 
  value = '', 
  onChange, 
  onComplete,
  disabled = false,
  className 
}) => {
  const [otp, setOtp] = useState(new Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    // Sync with external value
    if (value) {
      const valueArray = value.split('').slice(0, length);
      const newOtp = [...new Array(length).fill('')];
      valueArray.forEach((char, i) => {
        newOtp[i] = char;
      });
      setOtp(newOtp);
    } else {
      setOtp(new Array(length).fill(''));
    }
  }, [value, length]);

  const handleChange = (index, e) => {
    const val = e.target.value;
    
    // Only allow numbers
    if (val && !/^\d$/.test(val)) return;
    
    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);
    
    const otpString = newOtp.join('');
    onChange?.(otpString);
    
    // Move to next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto submit when complete - check if all 6 digits are filled
    if (val && newOtp.every(digit => digit !== '')) {
      // Use setTimeout to ensure state is updated before callback
      setTimeout(() => {
        const finalCode = newOtp.join('');
        if (finalCode.length === length) {
          onComplete?.(finalCode);
        }
      }, 50);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        // Move to previous input on backspace if current is empty
        inputRefs.current[index - 1]?.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
        onChange?.(newOtp.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    
    if (pastedData) {
      const newOtp = [...new Array(length).fill('')];
      pastedData.split('').forEach((char, i) => {
        newOtp[i] = char;
      });
      setOtp(newOtp);
      
      const otpString = newOtp.join('');
      onChange?.(otpString);
      
      // Focus last filled input or first empty
      const lastFilledIndex = Math.min(pastedData.length, length) - 1;
      inputRefs.current[lastFilledIndex]?.focus();
      
      // Auto submit if complete
      if (pastedData.length >= length) {
        onComplete?.(otpString);
      }
    }
  };

  const handleFocus = (index) => {
    inputRefs.current[index]?.select();
  };

  return (
    <div className={cn("flex gap-2 justify-center", className)}>
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={cn(
            "w-12 h-14 text-center text-2xl font-semibold rounded-lg border-2 transition-all",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            "bg-background border-border",
            digit ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : "",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          data-testid={`otp-input-${index}`}
        />
      ))}
    </div>
  );
};

export default OTPInput;
